/**
 * Keeps exactly one parsed source file hot, so decoding N frames of an instance
 * costs one file read + one `dicomParser.parseDicom`, not N of them.
 *
 * Why this sits ON TOP of WADO's `dataSetCacheManager` rather than replacing it:
 * `createImage` resolves `imagePlaneModule` / `voiLutModule` / `modalityLutModule`
 * / `sopCommonModule` through `cornerstone.metaData`, and WADO's metaData provider
 * answers those by looking the dataSet up in `dataSetCacheManager` by url. If the
 * parsed file isn't resident *at decode time*, the frame comes back silently
 * missing pixel spacing and window/level. So the dataSet must live in WADO's cache;
 * this module just controls how long it stays there.
 *
 * Refcount contract with `dataSetCacheManager`: its `load()` bumps an internal
 * `cacheCount` on every call and `unload()` decrements it, freeing at zero. We call
 * `load` exactly once per slot and `unload` exactly once, so our accounting is
 * always +1/-1 and never fights anything else that might be holding a reference.
 *
 * Eviction policy: a slot is freed as soon as its pin count hits zero, except for
 * the single most-recently-released one, which lingers. That "hot" slot is what
 * makes scrolling within a multiframe instance free — each frame acquires and
 * releases, but the parse survives between them.
 */

// Imported directly rather than via cornerstone-setup.js to stay out of the
// loader<->setup import cycle. Same module singleton either way, and only the
// `wadouri` namespace is touched at module scope.
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";
import * as fileRegistry from "./file-registry";
import { noteSourceBytes } from "./cache-budget";

const wadoLoader = cornerstoneWADOImageLoader as any;
const { dataSetCacheManager, parseImageId } = wadoLoader.wadouri;

interface Slot {
  url: string;
  promise: Promise<any>;
  pins: number;
  /** True when we were the one who called `load` and therefore owe an `unload`. */
  owned: boolean;
  bytes: number;
}

const slots = new Map<string, Slot>();
/** The one slot allowed to stay resident at pins === 0. */
let hotUrl: string | null = null;

/** The `dataSetCacheManager` key for an imageId — the bare url, frame suffix stripped. */
export function urlFor(imageId: string): string {
  return parseImageId(imageId).url;
}

/**
 * True when this imageId's bytes come from a local file we can re-read.
 *
 * Only these benefit from pinning. `wadouri:` instances are fetched over the
 * network by WADO's own loader, which keeps its dataSet cached across frames on
 * its own terms — pinning them would add nothing and would mean two components
 * managing the same refcount.
 */
export function isFileBacked(imageId: string): boolean {
  return imageId.startsWith("dicomfile:");
}

function byteLoaderFor(url: string, bytes?: ArrayBuffer) {
  // Caller already has the bytes in hand (the load step reads each file once and
  // feeds them to both dcmjs and dicom-parser) — don't read the file twice.
  if (bytes) return () => Promise.resolve(bytes);
  // A `dicomfile:` url resolves to the File the user dropped in.
  if (fileRegistry.hasUrl(url)) return () => fileRegistry.readBytesByUrl(url);
  // Anything else is a `wadouri:` URL — returning undefined lets
  // dataSetCacheManager fall back to its own xhrRequest, which is correct here.
  return undefined;
}

/**
 * Tell the cache budget how much memory our resident parses are taking, so the
 * frame cache can shrink to match. Called once per settled batch of changes
 * rather than per slot, since lowering the limit triggers a cache purge.
 */
function syncBudget(): void {
  let bytes = 0;
  for (const slot of slots.values()) bytes += slot.bytes;
  noteSourceBytes(bytes);
}

function discard(url: string): void {
  const slot = slots.get(url);
  if (!slot) return;
  slots.delete(url);
  if (hotUrl === url) hotUrl = null;
  if (slot.owned) {
    try {
      dataSetCacheManager.unload(url);
    } catch {
      // already gone — fine
    }
  }
}

/** Free every slot that isn't pinned and isn't the hot one. */
function evictIdle(): void {
  for (const [url, slot] of [...slots]) {
    if (slot.pins === 0 && url !== hotUrl) discard(url);
  }
  syncBudget();
}

/**
 * Pin the parsed dataSet for `url`, loading it if it isn't resident. Every
 * `acquire` must be matched by exactly one `release`, including on the error path
 * — put the release in a `finally`.
 *
 * `bytes` is an optional already-read buffer for the file; pass it when you have
 * one so the file isn't read from disk a second time.
 */
export async function acquire(
  url: string,
  imageId: string,
  bytes?: ArrayBuffer
): Promise<any> {
  let slot = slots.get(url);

  if (!slot) {
    // If something else already has this dataSet resident, don't take ownership of
    // it — we'd unload a reference we never loaded.
    const owned = !dataSetCacheManager.isLoaded(url);
    const promise = owned
      ? dataSetCacheManager.load(url, byteLoaderFor(url, bytes), imageId)
      : Promise.resolve(dataSetCacheManager.get(url));
    slot = { url, promise, pins: 0, owned, bytes: 0 };
    slots.set(url, slot);
  }

  slot.pins += 1;
  try {
    const dataSet = await slot.promise;
    slot.bytes = dataSet?.byteArray?.length ?? 0;
    // The frame cache gives up whatever this parse takes. Doing it here rather
    // than at load() means a big cine shrinks the cache as soon as it's resident.
    syncBudget();
    return dataSet;
  } catch (err) {
    // A failed parse must not leave a poisoned slot behind — the next acquire
    // should get a fresh attempt rather than the cached rejection.
    slot.pins -= 1;
    discard(url);
    syncBudget();
    throw err;
  }
}

/**
 * Drop one pin. At zero this url becomes the hot slot and every other idle slot is
 * freed, so at most one unpinned dataSet stays resident.
 */
export function release(url: string): void {
  const slot = slots.get(url);
  if (!slot) return;
  slot.pins -= 1;
  if (slot.pins > 0) return;
  slot.pins = 0;
  hotUrl = url;
  evictIdle();
}

/**
 * Free everything, including the hot slot. Call at phase boundaries (end of load,
 * viewer unmount, end of the export write pass).
 *
 * Deliberately not `dataSetCacheManager.purge()`: purge reassigns its internal maps
 * without zeroing `cacheSizeInBytes`, and it strands any in-flight `load()` whose
 * success callback then writes into the replaced map with a stale cacheCount.
 */
export function releaseAll(): void {
  hotUrl = null;
  for (const url of [...slots.keys()]) discard(url);
  syncBudget();
}

/**
 * Free every idle slot *except* the hot one — i.e. exactly what `release` already
 * leaves behind. Use this at a phase boundary where the next phase is likely to
 * want the same file back: dropping it there just means reading and re-parsing it
 * off disk moments later, and for a single large instance that's the whole study.
 */
export function releaseIdle(): void {
  evictIdle();
}

export function isResident(url: string): boolean {
  return slots.has(url);
}

export function stats(): { resident: number; pinned: number; bytes: number } {
  let pinned = 0;
  let bytes = 0;
  for (const slot of slots.values()) {
    if (slot.pins > 0) pinned += 1;
    bytes += slot.bytes;
  }
  return { resident: slots.size, pinned, bytes };
}
